import * as React from 'react';
// Import material ui modules
import {
	ArrowForward,
	ArrowBack,
	Check,
	Domain,
} from '@mui/icons-material';
// Import hooks.
import {
	useFormRef,
	UseFormRefResults,
} from '@app/hooks/view';
import useListBuilding, {
	UseListBuildingResults
} from './UseListBuilding';
// Import props.
import {
	FormPopupConfirmationProps,
	ListBuildingsProps,
	ListProps,
	NavigationFooterProps,
	ScrollbarProps,
	StatusProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
	NavigationFooterAlignment,
} from '@app/enumerations';
// Import api models.
import {
	BuildingRequestCommercialExisting,
	BuildingResponseCommercialExistingWithStatus,
} from '@app/models/api';

interface UseListBuildingsParameters {
	id: string;
	props: ListBuildingsProps,
}

interface UseListBuildingsResults {
	buildingList: UseListBuildingResults;
	applyFormProps: () => ListProps & ScrollbarProps;
	applyNavigationFooterProps: () => NavigationFooterProps;
	applyResubmitBuildingsFormPopupConfirmationProps: () => FormPopupConfirmationProps;
	applyAddBuildingFormPopupConfirmationProps: () => FormPopupConfirmationProps;
	applyDeleteBuildingFormPopupConfirmationProps: () => FormPopupConfirmationProps;
}

const useListBuildings = (params: UseListBuildingsParameters): UseListBuildingsResults => {

	// #region exported methods

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): ListProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
			title: 'Buildings',
			emptyBackgroundIcon: Domain,
			emptyBackgroundMessage: 'There are no buildings',
			onAddClick: params.props.allowEdits ? addBuilding : undefined,
			onRefreshClick: params.props.onRefreshBuildings,
			themeType: AppThemeColor.PRIMARY,
			fixedNavigation: true,
			statusProps: applyStatusProps(),
			...formRef.applyProps(),
		};
	};

	/**
	 * Returns props for the navigation footer. 
	 **/
	const applyNavigationFooterProps = (): NavigationFooterProps => {
		let isResubmitRequired: boolean = isResubmissionRequired();
		return {
			id: generateIdForNavigationFooter(),
			leftButtonDisabled: false,
			leftButtonLabel: 'Back',
			leftButtonClick: params.props.onBack,
			leftButtonIcon: ArrowBack,
			rightButtonDisabled: false,
			rightButtonLabel: isResubmitRequired ? 'Resubmit' : 'Continue',
			rightButtonClick: isResubmitRequired ? resubmitBuildings : params.props.onForward,
			rightButtonIcon: isResubmitRequired ? Check : ArrowForward,
			alignment: NavigationFooterAlignment.SPACE_BETWEEN,
		};
	};

	/**
	 * Returns props for the resubmit buildings confirmation popup form. 
	 **/
	const applyResubmitBuildingsFormPopupConfirmationProps = (): FormPopupConfirmationProps => {
		return {
			isOpen: openResubmitBuildingsConfirmationPopup,
			onClose: cancelConfirmResubmitBuildings,
			onConfirm: confirmResubmitBuildings,
			message: params.props.confirmationSaveMessage ?? String.empty(),
		};
	};

	/**
	 * Returns props for the add building confirmation popup form. 
	 **/
	const applyAddBuildingFormPopupConfirmationProps = (): FormPopupConfirmationProps => {
		return {
			isOpen: openAddBuildingConfirmationPopup,
			onClose: cancelConfirmAddBuilding,
			onConfirm: confirmAddBuilding,
			message: params.props.confirmationSaveMessage ?? String.empty(),
		};
	};

	/**
	 * Returns props for the delete building confirmation popup form. 
	 **/
	const applyDeleteBuildingFormPopupConfirmationProps = (): FormPopupConfirmationProps => {
		return {
			isOpen: openDeleteBuildingConfirmationPopup,
			onClose: cancelConfirmDeleteBuilding,
			onConfirm: confirmDeleteBuilding,
			message: params.props.confirmationDeleteBuildingMessage ?? String.empty(),
		};
	};

	// #endregion exported methods


	// #region private methods

	/**
	 * Returns props for the status. 
	 **/
	const applyStatusProps = (): StatusProps | undefined => {
		let statusProps: StatusProps | undefined = undefined;
		if (showStatusMessage) {
			statusProps = {
				statusMessage: params.props.statusMessage ?? String.empty(),
				statusType: AppThemeColor.ERROR,
			}
		}

		return statusProps;
	}

	// #region resubmit buildings

	/**
	 *  Starts the resubmit buildings process.
	 **/
	const resubmitBuildings = (): void => {
		// Make sure the confirmation popup appears if it's required.
		if (requireConfirmationOnSave) {
			setOpenResubmitBuildingsConfirmationPopup(true);
		}
		else {
			params.props.onResubmitBuildings(getBuildingScheduleForResubmission());
		}
	}

	/**
	 * Confirms resubmit buildings if user chooses to.
	 **/
	const confirmResubmitBuildings = (): void => {
		setOpenResubmitBuildingsConfirmationPopup(false);
		params.props.onResubmitBuildings(getBuildingScheduleForResubmission());
	}

	/**
	 * Cancels the confirmation prompt for resubmitting the buildings. 
	 **/
	const cancelConfirmResubmitBuildings = (): void => {
		setOpenResubmitBuildingsConfirmationPopup(false);
	}

	// #endregion resubmit buildings

	// #region add building

	/**
	 *  Starts the add building process.
	 **/
	const addBuilding = (): void => {
		// Make sure the confirmation popup appears if it's required.
		if (requireConfirmationOnSave) {
			setOpenAddBuildingConfirmationPopup(true);
		}
		else {
			params.props.onAddBuilding();
		}
	}

	/**
	 * Confirms building add if user chooses to.
	 **/
	const confirmAddBuilding = (): void => {
		setOpenAddBuildingConfirmationPopup(false);
		params.props.onAddBuilding();
	}

	/**
	 * Cancels the confirmation prompt for adding a buidling. 
	 **/
	const cancelConfirmAddBuilding = (): void => {
		setOpenAddBuildingConfirmationPopup(false);
	}

	// #endregion add building

	// #region delete building

	/**
	 *  Starts the delete building process.
	 **/
	const deleteBuilding = (buildingGuid: string): void => {
		// Make sure the confirmation popup appears if it's required.
		if (requireConfirmationOnDeleteBuilding) {
			setOpenDeleteBuildingConfirmationPopup(true);
			setBuildingGuidToBeDeleted(buildingGuid);
		}
		else {
			params.props.onDeleteBuilding(buildingGuid);
		}
	}

	/**
	 * Confirms building delete if user chooses to.
	 **/
	const confirmDeleteBuilding = (): void => {
		const buildingGuid: string | undefined = buildingGuidToBeDeleted;
		if (buildingGuid !== undefined) {
			setOpenDeleteBuildingConfirmationPopup(false);
			setBuildingGuidToBeDeleted(undefined);
			params.props.onDeleteBuilding(buildingGuid);
		}
	}

	/**
	 * Cancels the confirmation prompt for deleting a building. 
	 **/
	const cancelConfirmDeleteBuilding = (): void => {
		setOpenDeleteBuildingConfirmationPopup(false);
		setBuildingGuidToBeDeleted(undefined);
	}

	// #endregion delete building

	//#region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the navigation footer. 
	 **/
	const generateIdForNavigationFooter = (): string => {
		return `${generateIdForForm()}-navigationFooter`;
	}

	/**
	 * Generates id for building list item.
	 **/
	const generateIdForBuildingList = (): string => {
		return `${generateIdForForm()}-buildingList`;
	}

	//#endregion id generators

	/**
	 * Checks to see if the form requires resubmission. 
	 **/
	const isResubmissionRequired = (): boolean => {
		return params.props.allowEdits
			&& params.props.isReviewRequired;
	}

	/**
	 * Converts fetches the building schedule for resubmission. 
	 **/
	const getBuildingScheduleForResubmission = (): BuildingRequestCommercialExisting[] => {
		return params.props.buildings.map((building: BuildingResponseCommercialExistingWithStatus): BuildingRequestCommercialExisting => {
			return {
				id: building.id,
				buildingData: building.buildingData,
				address: null,
				limits: null,
			};
		});
	}
	// #endregion private methods


	// #region variables

	let requireConfirmationOnSave: boolean = params.props.requireConfirmationOnSave ?? false;
	let requireConfirmationOnDeleteBuilding: boolean = params.props.requireConfirmationOnDeleteBuilding ?? false;

	// Instantiate the form ref hook.
	const formRef: UseFormRefResults = useFormRef();

	// Instantiate the 'building' list hook.
	const buildingList: UseListBuildingResults = useListBuilding({
		id: generateIdForBuildingList(),
		disabled: !params.props.allowEdits,
		buildings: params.props.buildings,
		onViewBuilding: params.props.onViewBuilding,
		onDeleteBuilding: deleteBuilding,
	});

	const [buildingGuidToBeDeleted, setBuildingGuidToBeDeleted] = React.useState<string | undefined>(undefined);
	const [openAddBuildingConfirmationPopup, setOpenAddBuildingConfirmationPopup] = React.useState<boolean>(false)
	const [openResubmitBuildingsConfirmationPopup, setOpenResubmitBuildingsConfirmationPopup] = React.useState<boolean>(false)
	const [openDeleteBuildingConfirmationPopup, setOpenDeleteBuildingConfirmationPopup] = React.useState<boolean>(false)
	const [showStatusMessage, setShowStatusMessage] = React.useState<boolean>(false);

	// #endregion variables


	// #region life-cycle methods.

	// Called when the hook is initially mounted, and returns a call back that will be called on unmounting.
	React.useEffect(() => {
		if (params.props.buildings.length > 0) {
			let buildingGuid: string | undefined = params.props.buildings[0]?.id ?? undefined;
			if (buildingGuid !== undefined) {
				buildingList.focusToListItemRecord(buildingGuid);
			}
		}
		else {
			formRef.focusToForm();
		}
	}, []);

	// Called when updates are made the props related to the status of the form.
	React.useEffect(() => {
		// Determine if the status message should be rendered.
		if (params.props.isReviewRequired
			&& params.props.statusMessage !== undefined
			&& params.props.allowEdits) {
			setShowStatusMessage((currentShowStatusMessage: boolean) => {
				return true;
			});
		}
		else {
			setShowStatusMessage((currentShowStatusMessage: boolean) => {
				return false;
			});
		}
	}, [params.props.isReviewRequired, params.props.statusMessage, params.props.allowEdits]);

	// #endregion life-cycle methods.


	return {
		buildingList: buildingList,
		applyFormProps: applyFormProps,
		applyNavigationFooterProps: applyNavigationFooterProps,
		applyResubmitBuildingsFormPopupConfirmationProps: applyResubmitBuildingsFormPopupConfirmationProps,
		applyAddBuildingFormPopupConfirmationProps: applyAddBuildingFormPopupConfirmationProps,
		applyDeleteBuildingFormPopupConfirmationProps: applyDeleteBuildingFormPopupConfirmationProps,
	};
}

// Export parameters.
export {
	UseListBuildingsParameters,
}

// Export results.
export {
	UseListBuildingsResults,
}

export default useListBuildings;